import { Alert, Button, Group, Modal, Radio, Text, Textarea } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EXIT_REASONS, exitReasonLabel } from 'src/Components/Inventory/inventoryLabels';
import { useExitInventoryItem } from 'src/Hooks/Mutations/useInventoryMutations';
import { InventoryExitReason } from 'src/Services/API/generated';
import { trackInventoryItemExited } from 'src/Services/Mixpanel/events';

import styles from './InventoryExitModal.module.scss';
import { Props } from './InventoryExitModal.types';

/** What happened to it: one of six reasons, today by default, a word if wanted. */
const InventoryExitModal = ({ itemId, itemName, isManager, opened, onClose }: Props) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<InventoryExitReason | null>(null);
  const [date, setDate] = useState<Date | null>(new Date());
  const [note, setNote] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutate, isLoading } = useExitInventoryItem(itemId);

  const confirm = () => {
    setError(null);
    if (!reason) {
      setReasonError(t('inventory.exit.reasonRequired', 'Choisissez ce qui lui est arrivé.'));
      return;
    }
    mutate(
      {
        reason,
        date: dayjs(date ?? new Date()).format('YYYY-MM-DD'),
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          trackInventoryItemExited({ reason });
          onClose();
        },
        onError: () =>
          setError(t('inventory.exit.error', "L'objet n'a pas pu être sorti. Réessayez.")),
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('inventory.exit.title', 'Sortir {{name}} de l’inventaire', { name: itemName })}
    >
      <div className={styles.container}>
        <Radio.Group
          label={t('inventory.exit.question', 'Que lui est-il arrivé ?')}
          value={reason}
          error={reasonError}
          required
          onChange={value => {
            setReason(value as InventoryExitReason);
            setReasonError(null);
          }}
        >
          <div className={styles.reasons}>
            {EXIT_REASONS.map(value => (
              <Radio key={value} value={value} label={exitReasonLabel(t, value)} />
            ))}
          </div>
        </Radio.Group>
        <DateInput
          label={t('inventory.exit.date', 'Date')}
          value={date}
          onChange={setDate}
          maxDate={new Date()}
          valueFormat="D MMMM YYYY"
        />
        <Textarea
          autosize
          minRows={2}
          label={t('inventory.exit.note', 'Un mot')}
          placeholder={t('inventory.exit.notePlaceholder', 'Ex. Vendu au voisin')}
          value={note}
          onChange={event => setNote(event.currentTarget.value)}
        />
        {(reason === InventoryExitReason.Broken || reason === InventoryExitReason.LostOrStolen) && (
          <Alert color="orange">
            {isManager
              ? t(
                  'inventory.exit.claimHint',
                  'Si c’est un sinistre, gardez cette fiche : photos, factures et valeur serviront à votre déclaration. Vous pourrez la télécharger depuis la fiche.'
                )
              : t(
                  'inventory.exit.claimHintMember',
                  'Si c’est un sinistre, prévenez un gestionnaire : la fiche servira à la déclaration.'
                )}
          </Alert>
        )}
        <Text size="sm" c="dimmed">
          {t(
            'inventory.exit.kept',
            'L’objet restera visible dans « Objets sortis », avec ses photos, ses factures et ses liens. Il ne compte plus dans le dossier pour l’assurance.'
          )}
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button loading={isLoading} onClick={confirm}>
            {t('inventory.item.exit', 'Sortir de l’inventaire')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default InventoryExitModal;
